import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft,
  AlertTriangle,
  Calendar,
  Camera,
  CheckCircle,
  ChevronRight,
  Euro,
  Loader2,
  MessageSquare,
  ThumbsDown,
  ThumbsUp,
  User as UserIcon,
  X,
  XCircle,
} from 'lucide-react';
import Sidebar from '../components/Sidebar';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const API = process.env.REACT_APP_BACKEND_URL || '';

const getPhotoUrl = (p) => {
  if (!p) return '';
  if (p.startsWith('http')) return p;
  const token = localStorage.getItem('token');
  return `${API}/api/photo?path=${encodeURIComponent(p)}&auth=${token}`;
};

const formatDateFr = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
};

const AdminFdmProfile = () => {
  const { userId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [fdm, setFdm] = useState(null);
  const [feedbacks, setFeedbacks] = useState([]);
  const [stats, setStats] = useState({ total_rejections: 0, total_lost: 0, evaluations_count: 0 });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [userRes, evalRes, rejRes] = await Promise.all([
        axios.get(`${API}/api/users`, { headers }),
        axios.get(`${API}/api/users/${userId}/evaluations`, { headers }),
        axios.get(`${API}/api/users/${userId}/rejections`, { headers }),
      ]);

      const target = (userRes.data || []).find((u) => u.id === userId);
      setFdm(target || null);

      const evalList = (evalRes.data?.evaluations || []).map((e) => ({
        type: 'evaluation',
        task_id: e.task_id,
        property_name: e.property_name,
        date: e.date,
        comment: e.comment,
        rating: e.rating,
        result: e.result,
        photos: e.photos || [],
        sorted_at: e.commented_at,
        author: e.commented_by,
      }));

      const rejList = (rejRes.data?.rejections || []).map((r) => ({
        type: 'rejection',
        task_id: r.task_id,
        property_name: r.property_name,
        date: r.date,
        comment: r.reason,
        rating: null,
        result: 'rejected',
        photos: r.photos || [],
        sorted_at: r.rejected_at,
        author: r.rejected_by,
      }));

      const merged = [...rejList, ...evalList].sort((a, b) => {
        const ta = a.sorted_at ? new Date(a.sorted_at).getTime() : 0;
        const tb = b.sorted_at ? new Date(b.sorted_at).getTime() : 0;
        return tb - ta;
      });
      setFeedbacks(merged);

      setStats({
        total_rejections: rejList.length,
        total_lost: 0, // montant perdu : on n'a pas price dans l'API user/rejections
        evaluations_count: evalList.length,
      });
    } catch (e) {
      toast.error('Erreur lors du chargement du profil');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <div className="flex min-h-screen bg-slate-50">
        <Sidebar />
        <main className="flex-1 md:ml-72 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar />
      <main className="flex-1 md:ml-72 p-4 md:p-8">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/admin/users')}
            data-testid="back-to-users"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl md:text-3xl font-black text-[#0F172A]">
              {fdm?.name || 'Profil FDM'}
            </h1>
            <p className="text-slate-500 text-sm">
              {fdm?.email}{fdm?.phone ? ` · ${fdm.phone}` : ''}
            </p>
          </div>
        </div>

        {/* KPI */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card className="border-red-200">
            <CardContent className="p-5 flex items-center gap-4">
              <div className="bg-red-100 p-3 rounded-xl">
                <XCircle className="h-6 w-6 text-red-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide font-semibold">Rejets</p>
                <p className="text-3xl font-black text-[#0F172A]" data-testid="kpi-rejections">
                  {stats.total_rejections}
                </p>
                <p className="text-xs text-slate-500">prestations refusées</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-5 flex items-center gap-4">
              <div className="bg-emerald-100 p-3 rounded-xl">
                <ThumbsUp className="h-6 w-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide font-semibold">Contrôles</p>
                <p className="text-3xl font-black text-[#0F172A]" data-testid="kpi-evaluations">
                  {stats.evaluations_count}
                </p>
                <p className="text-xs text-slate-500">contrôles qualité</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-5 flex items-center gap-4">
              <div className="bg-slate-100 p-3 rounded-xl">
                <UserIcon className="h-6 w-6 text-slate-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide font-semibold">Statut</p>
                <p className="text-lg font-bold text-[#0F172A] mt-1">
                  {fdm?.is_active === false ? 'Inactif' : 'Actif'}
                </p>
                <p className="text-xs text-slate-500">
                  {fdm?.role === 'housekeeper' ? 'Femme de ménage' : fdm?.role}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Retours */}
        <section>
          <h2 className="text-lg font-bold text-[#0F172A] mb-3 flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-[#1F3A8A]" />
            Retours — rejets &amp; contrôles qualité ({feedbacks.length})
          </h2>

          {feedbacks.length === 0 ? (
            <Card>
              <CardContent className="py-16 text-center">
                <CheckCircle className="h-12 w-12 mx-auto text-slate-200 mb-3" />
                <p className="text-slate-600 font-semibold">Aucun retour pour l'instant</p>
                <p className="text-sm text-slate-400 mt-1">
                  Les rejets et contrôles qualité apparaîtront ici.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="flex flex-col gap-4">
              {feedbacks.map((item) => {
                const isRejection = item.type === 'rejection';
                return (
                  <Card
                    key={`${item.type}-${item.task_id}`}
                    className={`cursor-pointer transition-colors ${
                      isRejection ? 'border-red-200 hover:border-red-400' : 'hover:border-[#1F3A8A]'
                    }`}
                    onClick={() => navigate(`/admin/tasks/${item.task_id}`)}
                    data-testid={isRejection ? 'rejection-row' : 'evaluation-row'}
                  >
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-bold text-[#0F172A] text-base truncate">
                              {item.property_name}
                            </h3>
                            {isRejection ? (
                              <Badge className="bg-red-100 text-red-700 hover:bg-red-100">
                                <X className="h-3 w-3 mr-1" />Refusée
                              </Badge>
                            ) : item.result === 'validated' ? (
                              <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
                                <ThumbsUp className="h-3 w-3 mr-1" />Validé
                              </Badge>
                            ) : item.result ? (
                              <Badge className="bg-red-100 text-red-700 hover:bg-red-100">
                                <ThumbsDown className="h-3 w-3 mr-1" />Non-conforme
                              </Badge>
                            ) : null}
                          </div>

                          <div className="flex items-center gap-3 text-xs text-slate-500 mb-3">
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />{formatDateFr(item.date)}
                            </span>
                            {item.rating && (
                              <span className={`font-black ${
                                item.rating >= 8 ? 'text-emerald-700'
                                  : item.rating >= 5 ? 'text-amber-700' : 'text-red-700'
                              }`}>
                                {item.rating}/10
                              </span>
                            )}
                          </div>

                          {item.comment && (
                            <div className={`rounded-lg p-3 border mb-3 ${
                              isRejection ? 'bg-red-50 border-red-100' : 'bg-slate-50 border-slate-100'
                            }`}>
                              {isRejection && (
                                <p className="text-xs font-bold text-red-700 mb-1 uppercase">Motif</p>
                              )}
                              <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">
                                {item.comment}
                              </p>
                            </div>
                          )}

                          {item.photos && item.photos.length > 0 && (
                            <div className="flex items-center gap-2 mb-2">
                              <div className="flex gap-1.5">
                                {item.photos.slice(0, 5).map((p, i) => (
                                  <a
                                    key={i}
                                    href={getPhotoUrl(p)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    className="shrink-0"
                                  >
                                    <img
                                      src={getPhotoUrl(p)}
                                      alt=""
                                      className={`h-14 w-14 rounded-lg object-cover border ${
                                        isRejection ? 'border-red-200' : 'border-slate-200'
                                      }`}
                                    />
                                  </a>
                                ))}
                              </div>
                              <span className="text-xs text-slate-500 flex items-center gap-1">
                                <Camera className="h-3 w-3" />
                                {item.photos.length} photo{item.photos.length > 1 ? 's' : ''}
                              </span>
                            </div>
                          )}

                          <p className="text-xs text-slate-400">
                            {isRejection ? 'Refusé par' : 'Contrôle par'}{' '}
                            <strong>{item.author || 'EQUIPE MCB'}</strong>
                            {item.sorted_at && (
                              <> · {new Date(item.sorted_at).toLocaleDateString('fr-FR')}</>
                            )}
                          </p>
                        </div>
                        <ChevronRight className="h-5 w-5 text-slate-300 shrink-0" />
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </section>
      </main>
    </div>
  );
};

export default AdminFdmProfile;
